import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    makeStyles,
    Typography,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: '20px',
    },
    sectionTitle: {
        fontWeight: 'bold',
        marginBottom: '10px',
        marginTop: '20px',
    },
    breakdownItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '5px 0',
        borderBottom: '1px solid #f0f0f0',
    },
    breakdownLabel: {
        fontWeight: '500',
    },
    breakdownValue: {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    totalSection: {
        marginTop: '15px',
        paddingTop: '15px',
        borderTop: '2px solid #e0e0e0',
    },
    totalItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        fontSize: '16px',
        fontWeight: 'bold',
    },
}));

function BreakdownDialog({ breakdownType = 'lifetime', onClose, open, subscription }) {
    const classes = useStyles();

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value || 0);

    // Select the appropriate breakdown data based on breakdownType
    const creditBreakdown =
        breakdownType === 'ninety' ? subscription?.creditBreakdownNinety || {} : subscription?.creditBreakdown || {};
    const refundBreakdown =
        breakdownType === 'ninety' ? subscription?.refundBreakdownNinety || {} : subscription?.refundBreakdown || {};

    const totalCredits = Object.values(creditBreakdown).reduce((sum, value) => sum + (value || 0), 0);
    const totalRefunds = Object.values(refundBreakdown).reduce((sum, value) => sum + (value || 0), 0);

    const dialogTitle =
        breakdownType === 'ninety' ? '90 Day Credit and Refund Breakdown' : 'Lifetime Credit and Refund Breakdown';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Credit Breakdown
                        </Typography>
                        {Object.entries(creditBreakdown).map(([key, value]) => (
                            <div key={key} className={classes.breakdownItem}>
                                <span className={classes.breakdownLabel}>
                                    {key
                                        .replace(/_/g, ' ')
                                        .toLowerCase()
                                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                                <span className={classes.breakdownValue}>{formatCurrency(value)}</span>
                            </div>
                        ))}
                        <div className={classes.totalSection}>
                            <div className={classes.totalItem}>
                                <span>Total Credits</span>
                                <span>{formatCurrency(totalCredits)}</span>
                            </div>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Refund Breakdown
                        </Typography>
                        {Object.entries(refundBreakdown).map(([key, value]) => (
                            <div key={key} className={classes.breakdownItem}>
                                <span className={classes.breakdownLabel}>
                                    {key
                                        .replace(/_/g, ' ')
                                        .toLowerCase()
                                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                                <span className={classes.breakdownValue}>{formatCurrency(value)}</span>
                            </div>
                        ))}
                        <div className={classes.totalSection}>
                            <div className={classes.totalItem}>
                                <span>Total Refunds</span>
                                <span>{formatCurrency(totalRefunds)}</span>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export { BreakdownDialog };
